import { supabase } from '../lib/supabase';
import { withSupabaseQueryTimeout } from '../lib/supabaseTimeout';

const EVENT_SAVE_TIMEOUT_MS = 45000;

const parseDateParts = (dateValue) => {
  const value = String(dateValue || '').trim();
  if (!value) return null;

  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return {
      year: Number(isoMatch[1]),
      monthIndex: Number(isoMatch[2]) - 1,
      day: Number(isoMatch[3]),
    };
  }

  const usMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    return {
      year: Number(usMatch[3]),
      monthIndex: Number(usMatch[1]) - 1,
      day: Number(usMatch[2]),
    };
  }

  return null;
};

const parseTimeParts = (timeValue) => {
  const value = String(timeValue || '').trim();
  if (!value) return { hours: 0, minutes: 0 };

  const match = value.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[3]?.toUpperCase();

  if (meridiem === 'PM' && hours < 12) hours += 12;
  if (meridiem === 'AM' && hours === 12) hours = 0;

  if (hours > 23 || minutes > 59) return null;
  return { hours, minutes };
};

export const buildBoardMeetingScheduledAt = (dateValue, timeValue) => {
  const dateParts = parseDateParts(dateValue);
  if (!dateParts) return null;

  const timeParts = parseTimeParts(timeValue);
  if (!timeParts) return null;

  const date = new Date(
    dateParts.year,
    dateParts.monthIndex,
    dateParts.day,
    timeParts.hours,
    timeParts.minutes,
    0,
    0
  );

  if (
    Number.isNaN(date.getTime())
    || date.getFullYear() !== dateParts.year
    || date.getMonth() !== dateParts.monthIndex
    || date.getDate() !== dateParts.day
  ) {
    return null;
  }

  return date.toISOString();
};

export const buildBoardMeetingPayload = ({ hoaId, form }) => ({
  hoa_id: hoaId,
  title: form.title.trim(),
  meeting_type: form.meeting_type,
  scheduled_at: buildBoardMeetingScheduledAt(form.date, form.time),
  location: form.location.trim() || null,
  meeting_url: form.meeting_url.trim() || null,
  status: 'scheduled',
  visible_to_roles: form.visible_to_roles,
  updated_at: new Date().toISOString(),
});

export const saveBoardMeeting = async ({ hoaId, userId, form, editingEventId }) => {
  const payload = buildBoardMeetingPayload({ hoaId, form });

  if (editingEventId) {
    const result = await withSupabaseQueryTimeout(
      supabase
        .from('board_meetings')
        .update(payload)
        .eq('hoa_id', hoaId)
        .eq('id', editingEventId),
      'Saving event',
      EVENT_SAVE_TIMEOUT_MS
    );

    return { ...result, payload };
  }

  const result = await withSupabaseQueryTimeout(
    supabase
      .from('board_meetings')
      .insert({ ...payload, created_by: userId || null })
      .select('id, hoa_id, title, meeting_type, scheduled_at, location, meeting_url, status, visible_to_roles, created_by, created_at, updated_at')
      .single(),
    'Saving event',
    EVENT_SAVE_TIMEOUT_MS
  );

  return { ...result, payload };
};
